import { Dispatch, SetStateAction, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// local imports && assets
import successImage from 'assets/images/success-image.svg'
import calendarLoading from 'assets/icons/loading-calendar.png'
import DateUtil from 'utils/DateUtil'
import { IAvailableParkAreasReq } from 'types/ParkAreaType'
import Loader from 'components/ui/Loader'
import NotificationDialog from 'components/ui/NotificationDialog'
import useGetParkAreaList from '../hooks/use-get-park-area-list'
import useParkAreaResModal from '../hooks/use-park-area-res-modal'
import RedirectCommonOffice from './RedirectCommonOffice'
import ParkAreaFilterForm from './ParkAreaFilterForm'
import ParkAreaListRender from './ParkAreaListRender'
import ParkReservationModal from './ParkReservationModal'
import { Divider, FormLayoutWrapper, ParkAreaCardList } from './styled'

type StateType = {
  date: Date
}

/* filtered date - default */
const today = DateUtil.getStartOfDay(new Date())

export default function ParkAreaList({ redirectCommon }: { redirectCommon: Dispatch<SetStateAction<number>> }) {
  const { t } = useTranslation()
  const history = useHistory()
  const { state } = useLocation<StateType>()
  const dateFromPath = state?.date

  /* Filter form default values */
  const defaultValues: IAvailableParkAreasReq = {
    reservationDate: !!dateFromPath ? new Date(dateFromPath) : today,
  }

  /* Filter form state  */
  const { control, reset, handleSubmit, formState, getValues, watch } = useForm<IAvailableParkAreasReq>({
    defaultValues,
  })

  /* Form Actions | Methods | button disabled states */
  const { isDirty, submitCount } = formState
  const disableResetBtn = !isDirty && submitCount < 1
  const disableSubmitBtn = !isDirty && submitCount < 1
  const filteredDate = watch('reservationDate') || today

  /* Get Park Area List - Locations | Custom Hooks */
  const { getParkAreaList, availableParkAreas, loading } = useGetParkAreaList()

  /* Handle Form Submit */
  const onSubmit = (data: IAvailableParkAreasReq) => {
    getParkAreaList(data.reservationDate)
  }
  const runAfterSubmit = () => getParkAreaList(getValues()['reservationDate'])

  /* Handle Reset Filter Form */
  const handleResetForm = () => {
    reset({ reservationDate: today }, { keepDirtyValues: false, keepDirty: false, keepDefaultValues: false })
    !!submitCount && onSubmit(defaultValues)
  }

  /* Get Park Area List on initial page load  */
  useEffect(() => {
    onSubmit(defaultValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* Reservation Modal */
  const { modal: isOpen, openModal, closeModal, notification, setNotification, selectedPark } = useParkAreaResModal()

  const closeNotification = () => {
    runAfterSubmit()
    setNotification(false)
  }

  const redirectToCommonTab = () => redirectCommon((prev) => prev - 1)

  /* Show full-screen loader only on initial load (when list is empty).
     During background refresh after a reservation, keep cards/notification mounted. */
  if (loading && !availableParkAreas) return <ListLoader />

  return (
    <div className="campus-services">
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLayoutWrapper>
          <ParkAreaFilterForm
            control={control}
            disableResetBtn={disableResetBtn}
            disableSubmitBtn={disableSubmitBtn}
            handleReset={handleResetForm}
          />
        </FormLayoutWrapper>
      </form>

      <Divider />

      {/* Render Card List — keep mounted during refresh so the success notification
          dialog (rendered below) is not unmounted. */}
      <ParkAreaCardList>
        {!availableParkAreas ? (
          <RedirectCommonOffice handleRedirect={redirectToCommonTab} />
        ) : (
          <ParkAreaListRender availableParkAreas={availableParkAreas} openModal={openModal} />
        )}
      </ParkAreaCardList>

      {/* Park Area Reservation Modal */}
      {!!isOpen && selectedPark ? (
        <ParkReservationModal
          closeModal={closeModal}
          open={isOpen}
          showNotification={() => {
            runAfterSubmit()
            setNotification(true)
          }}
          selectedParkArea={selectedPark}
          filterDate={filteredDate}
        />
      ) : null}

      {/* Notification - modal - after successful reservation */}
      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('park-area-res-modal.notification-title')}
        content={t('park-area-res-modal.notification-text')}
        firstButtonLabel={t('buttons.go-to-calendar')}
        onHide={closeNotification}
        onSubmit={() => history.push('/calendar')}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </div>
  )
}

function ListLoader() {
  return (
    <div className="py-5 my-4">
      <Loader src={calendarLoading} />
    </div>
  )
}
